"use client";

import { useMemo, useState, useEffect } from "react";
import { 
  Bell, 
  User, 
  Menu,
  ChevronDown,
  LogOut,
  Key
} from "lucide-react";
import styles from "./Header.module.css";
import Image from "next/image";
import { useNotifications } from "../notifications/NotificationsProvider";
import { useRouter } from "next/navigation";
import authService, { Admin } from "../../../../services/authService";
import ProfileDrawer from "../profile/ProfileDrawer";
import type { AdminNotification } from "../notifications/NotificationsProvider";

interface DashboardHeaderProps {
  title?: string;
  onMenuToggle?: () => void;
  showMenuToggle?: boolean;
}

export default function DashboardHeader({ 
  title = "Dashboard", 
  onMenuToggle,
  showMenuToggle = false 
}: DashboardHeaderProps) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileDrawer, setShowProfileDrawer] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [admin, setAdmin] = useState<Admin | null>(null);
  const router = useRouter();
  const { notifications, unreadCount, markAllAsRead, markAsRead } = useNotifications();

  useEffect(() => {
    const loadAdmin = async () => {
      try {
        const adminData = await authService.getCurrentAdmin();
        if (adminData) {
          setAdmin(adminData);
        }
      } catch (error) {
        console.error('Error loading admin:', error);
      }
    };
    loadAdmin();
  }, []);

  const formatted: (AdminNotification & { time: string })[] = useMemo(
    () =>
      notifications.map((n: AdminNotification) => ({
        ...n,
        time: new Date(n.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      })),
    [notifications]
  );

  function navigateTo(itemHref: string | undefined, category: string) {
    const fallbackByCategory: Record<string, string> = {
      orders: "/admin/orders",
      schedules: "/admin/schedules",
      messages: "/admin/messages",
      news: "/admin/news",
      products: "/admin/products",
      services: "/admin/services",
      users: "/admin/users",
      other: "/admin",
    };
    const href = itemHref || fallbackByCategory[category] || "/admin";
    router.push(href as any);
  }

  const handleProfileClick = () => {
    setShowUserMenu(false);
    setShowChangePassword(false);
    setShowProfileDrawer(true);
  };

  const handleChangePasswordClick = () => {
    setShowUserMenu(false);
    setShowChangePassword(true);
    setShowProfileDrawer(true);
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    try {
      await authService.logout();
      router.push("/admin/login");
    } catch (error) {
      console.error("Logout error:", error);
      router.push("/admin/login");
    }
  };

  return (
    <header className={styles.dashboardHeader}>
      <div className={styles.dashboardHeaderLeft}>
        {showMenuToggle && (
          <button 
            className={styles.menuToggleBtn}
            onClick={onMenuToggle}
            aria-label="Mở/đóng menu"
          >
            <Menu size={20} />
          </button>
        )}
        <h1 className={styles.dashboardTitle}>{title}</h1>
      </div>

      <div className={styles.dashboardHeaderRight}>
        {/* Notifications */}
        <div className={styles.notificationsContainer}>
          <button 
            className={styles.notificationsBtn}
            onClick={() => setShowNotifications(!showNotifications)}
            aria-label="Thông báo"
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className={styles.notificationBadge}>{unreadCount}</span>
            )}
          </button>

          {showNotifications && (
            <div className={styles.notificationsDropdown}>
              <div className={styles.notificationsHeader}>
                <h3>Thông báo</h3>
                <span className={styles.unreadCount}>{unreadCount} chưa đọc</span>
              </div>
              <div className={styles.notificationsList}>
                {formatted.length === 0 && (
                  <div className={styles.notificationsEmpty}>
                    <p>Không có thông báo</p>
                  </div>
                )}
                {formatted.map((notification: AdminNotification & { time: string }) => (
                  <button
                    key={notification.id}
                    type="button"
                    className={`${styles.notificationItem} ${notification.unread ? styles.unread : ''}`}
                    onClick={() => {
                      markAsRead(notification.id);
                      navigateTo(notification.href, notification.category);
                      setShowNotifications(false);
                    }}
                  >
                    <div className={styles.notificationContent}>
                      <div className={styles.notificationHeader}>
                        <p className={styles.notificationTitle}>{notification.title}</p>
                        <span className={styles.notificationTime}>{notification.time}</span>
                      </div>
                      {notification.message && (
                        <p className={styles.notificationDescription}>{notification.message}</p>
                      )}
                    </div>
                    {notification.unread && <span className={styles.notificationIndicator} aria-hidden="true"></span>}
                  </button>
                ))}
              </div>
              <div className={styles.notificationsFooter}>
                <button className={styles.viewAllBtn} onClick={() => markAllAsRead()}>Đánh dấu tất cả đã đọc</button>
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className={styles.userMenuContainer}>
          <button 
            className={styles.userMenuBtn}
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-label="Menu người dùng"
          >
            <div className={styles.userAvatar}>
              <Image 
                src={admin?.avatar || "/assets/chat/default-avatar.svg"} 
                alt="Avatar" 
                width={32} 
                height={32}
                style={{ objectFit: 'cover' }}
              />
            </div>
            <span className={styles.userName}>{admin?.fullName || "Admin"}</span>
            <ChevronDown size={16} className={styles.userMenuArrow} />
          </button>

          {showUserMenu && (
            <div className={styles.userMenuDropdown}>
              <div className={styles.userInfo}>
                <div className={styles.userAvatarLarge}>
                  <Image 
                    src={admin?.avatar || "/assets/chat/default-avatar.svg"} 
                    alt="Avatar" 
                    width={40} 
                    height={40}
                    style={{ objectFit: 'cover' }}
                  />
                </div>
                <div className={styles.userDetails}>
                  <p className={styles.userNameLarge}>{admin?.fullName || "Admin"}</p>
                  <p className={styles.userEmail}>{admin?.email || ""}</p>
                </div>
              </div>
              <div className={styles.userMenuDivider}></div>
              <div className={styles.userMenuItems}>
                <button className={styles.userMenuItem} onClick={handleProfileClick}>
                  <User size={16} />
                  <span>Hồ sơ</span>
                </button>
                <button className={styles.userMenuItem} onClick={handleChangePasswordClick}>
                  <Key size={16} />
                  <span>Đổi mật khẩu</span>
                </button>
                <div className={styles.userMenuDivider}></div>
                <button className={`${styles.userMenuItem} ${styles.logout}`} onClick={handleLogout}>
                  <LogOut size={16} />
                  <span>Đăng xuất</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {showProfileDrawer && (
        <ProfileDrawer 
          isOpen={showProfileDrawer} 
          onClose={() => {
            setShowProfileDrawer(false);
            setShowChangePassword(false);
          }}
          defaultSection={showChangePassword ? 'password' : undefined}
        />
      )}
    </header>
  );
}

